import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export const PrivacyPolicyDialog = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="text-purple-600 hover:text-purple-700 underline bg-transparent border-none cursor-pointer">
          Privacy Policy
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center mb-4">
            Mayl AI Privacy Policy
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 text-sm leading-relaxed">
          <div className="text-center text-gray-600 font-medium">
            <p>Effective Date: June 23, 2025</p>
            <p>Last Updated: June 23, 2025</p>
          </div>
          
          <p>My People Know Inc. d/b/a Mayl AI ("Mayl AI," "we," "us," or "our") is committed to safeguarding your privacy. This Privacy Policy explains how we collect, use, disclose, and protect personal information in connection with our AI-powered email services (adding labels, removing labels, and drafting emails) built on Google Gemini models. It also describes your rights and how you can contact us.</p>

          <div>
            <h3 className="font-semibold text-base mb-2">1. Who We Are & Your Role</h3>
            <p><strong>Controller:</strong> My People Know Inc. d/b/a Mayl AI<br />
            <strong>Address:</strong> 100 King Street West, Suite 5700, Toronto, Ontario, Canada, M5X 1C7<br />
            <strong>Contact:</strong> privacy@mayl.ai</p>
            <p className="mt-2">When you sign up, you authorize Mayl AI to process data in accordance with this policy. You remain the "data controller" of your own email content; we act as a "data processor" strictly to provide the service.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">2. Information We Collect</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium">Account Information</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>Name, email address, profile picture (via Google OAuth)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium">Email Metadata & Content</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>Message IDs, subject lines, timestamps</li>
                  <li>Email body text, attachments (only as needed to draft responses)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium">Usage & Device Data</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>IP address, browser type, OS version, device identifiers</li>
                  <li>Service logs (API calls for labeling and drafting)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium">Preferences & Settings</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>Label color choices, personalization settings</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">3. How We Collect Data</h3>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong>User Consent & OAuth:</strong> When you connect your Gmail account via Google OAuth, you grant specific scopes to read and modify labels, read email content, and send draft messages.</li>
              <li><strong>API Calls:</strong> Our backend (AWS Lambdas, DynamoDB, SQS) pulls and processes only the data needed for each action.</li>
              <li><strong>Automatic Logging:</strong> We log service usage and errors for performance monitoring and troubleshooting.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">4. How We Use Your Information</h3>
            <p>We use your data to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Provide & Improve the Service</strong>
                <ul className="list-disc ml-6 mt-1">
                  <li>Automatically create, assign, and remove Gmail labels.</li>
                  <li>Generate draft emails tailored to your writing style.</li>
                </ul>
              </li>
              <li><strong>Maintain Security & Reliability</strong>
                <ul className="list-disc ml-6 mt-1">
                  <li>Monitor for errors, outages, and misuse.</li>
                  <li>Authenticate requests via Google OAuth tokens stored securely in AWS KMS and Secrets Manager.</li>
                </ul>
              </li>
              <li><strong>Compliance & Support</strong>
                <ul className="list-disc ml-6 mt-1">
                  <li>Respond to legal requests or abuses.</li>
                  <li>Offer customer support.</li>
                </ul>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">5. Data Sharing & Third Parties</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium">AI Processing</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>Email content and metadata are sent in real-time to Google's Gemini 2.5 models.</li>
                  <li>No data is persisted by Google beyond the API call (per Google's data protection terms).</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium">Infrastructure Partners</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>AWS (hosting, storage, encryption)</li>
                  <li>Supplied AWS sub-processors (CloudWatch, DynamoDB, SQS)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium">Legal & Compliance</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>Disclosures required by law or to defend our legal rights.</li>
                </ul>
              </div>
            </div>
            <p className="mt-3">We do not sell or rent your personal data. All third parties are contractually bound to process data only as instructed by Mayl AI.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">6. Cross-Border Transfers</h3>
            <p>Your data may flow through or be stored in AWS regions in the U.S. or elsewhere. We protect these transfers by:</p>
            <ul className="list-disc ml-6 mt-2">
              <li>Standard Contractual Clauses (SCCs) where required</li>
              <li>Encryption in transit (TLS) and at rest (AES-256)</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">7. Data Retention</h3>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong>Label & Draft Metadata:</strong> Retained as long as your account is active, then purged within 30 days of account deletion.</li>
              <li><strong>Email Content:</strong> Cached only briefly (seconds) for AI processing and never stored long-term.</li>
              <li><strong>Logs & Metrics:</strong> Retained for up to 90 days for debugging and analytics, then aggregated or deleted.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">8. Security Measures</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium">Authentication & Encryption</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>Google OAuth 2.0 for sign-in</li>
                  <li>AWS KMS keys for all secrets</li>
                  <li>HTTPS/TLS encryption end-to-end</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium">Access Controls & Auditing</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>Principle of least privilege (AWS IAM roles)</li>
                  <li>Regular security reviews and CloudWatch alerts</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium">Incident Response</h4>
                <ul className="list-disc ml-6 mt-1">
                  <li>Formal breach response plan</li>
                  <li>Notifications to affected users and regulators within 72 hours if required</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">9. Your Choices & Rights</h3>
            <p>Depending on your jurisdiction, you may have rights to:</p>
            <ul className="list-disc ml-6 mt-2">
              <li>Access, correct, or delete your personal data</li>
              <li>Restrict or object to processing</li>
              <li>Port your data in machine-readable form</li>
              <li>Withdraw consent at any time (your service access will end)</li>
            </ul>
            <p className="mt-2">To exercise your rights, contact us at privacy@mayl.ai. We aim to respond within 30 days.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">10. Children's Privacy</h3>
            <p>Mayl AI is not intended for children under 16. We do not knowingly collect data from minors. If you believe we have, contact us to have it deleted.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">11. Updates to This Policy</h3>
            <p>We may revise this policy to reflect changes in our service or legal obligations. We'll notify you by posting the new version on our website and updating the Last Updated date. Continued use after changes constitutes acceptance.</p>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold text-base mb-2">Contact Us</h3>
            <p>If you have any questions, concerns, or requests about this Privacy Policy, please email privacy@mayl.ai or write to us at:</p>
            <p className="mt-2">My People Know Inc. d/b/a Mayl AI<br />
            100 King Street West, Suite 5700<br />
            Toronto, Ontario, Canada, M5X 1C7</p>
            <p className="mt-4 font-medium">Thank you for trusting Mayl AI with your email workflow. We take your privacy seriously.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
